import { ColorWheelIcon } from '@radix-ui/react-icons'

import { getDocsPath } from 'lib/routes/replace-dynamic-paths'

import { DenoIcon } from 'components/atomic/atoms/icons/logos/deno'
import BackgroundSquares from 'components/atomic/atoms/svgs/background-squares'
import LayoutWebsite from 'components/pages/@layouts/layout-website'
import { GithubIcon } from 'components/pages/@shared/icons/github'
import { QuickstartIcon } from 'components/pages/@shared/icons/quickstart'
import SharedTemplate from 'components/pages/frameworks/@shared/template'
import { TemplateCodeExampleProps } from 'components/pages/frameworks/@shared/template/template.code-examples'

const framework = 'Deno'

const startBuilding = [
  {
    icon: QuickstartIcon,
    title: 'Quickstart',
    text: 'Learn the basics of Grafbase and deploy to production in only a few short steps.',
    href: getDocsPath('/quickstart/get-started'),
    buttonLabel: 'Get Started'
  },
  {
    icon: ColorWheelIcon,
    title: 'Developer Guides',
    text: 'Learn how to use Grafbase with frontend frameworks, external APIs, and more.',
    href: '/guides',
    buttonLabel: 'Read guides'
  },
  {
    icon: GithubIcon,
    title: 'Fresh Example',
    text: 'See how Grafbase works with Deno, Fresh and TypeScript in this example.',
    external: true,
    href: 'https://github.com/grafbase/grafbase/tree/main/examples/fresh',
    buttonLabel: 'See Source'
  }
]

const codeExamples: TemplateCodeExampleProps['examples'] = [
  {
    title: 'Fetch API',
    description: 'Fetch data from Grafbase using the native Fetch API.',
    language: 'tsx',
    code: `
const response = await fetch('GRAFBASE_API_URL', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': 'GRAFBASE_API_KEY'
    },
    body: JSON.stringify({
      query: /* GraphQL */ \`
        query GetAllPosts($first: Int!) {
          postCollection(first: $first) {
            edges {
              node {
                id
                title
              }
            }
          }
        }
      \`,
      variables: { first: 100 }
    })
  })

  const data = await response.json()
`
  },
  {
    title: 'Dynamic routes',
    description: 'Fetch data for dynamic routes at the edge with Fresh.',
    language: 'tsx',
    code: `
import { Handlers, PageProps } from '$fresh/server.ts'

const GetPostBySlugQuery = /* GraphQL */ \`
  query GetPostBySlug($slug: String!) {
    post(by: { slug: $slug }) {
      id
      title
    }
  }
\`

export const handler: Handlers = {
  async GET(_, ctx) {
    const { slug } = ctx.params

    const response = await fetch('GRAFBASE_API_URL', {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        'x-api-key': 'GRAFBASE_API_KEY'
      },
      body: JSON.stringify({
        query: GetPostBySlugQuery,
        variables: {
          slug
        }
      })
    })

    if (!response.ok) {
      return ctx.render(null)
    }

    const { data } = await response.json()

    return ctx.render(data)
  }
}

export default function PostPage({ data }: PageProps) {
  return <h1>{data.post.title}</h1>
}
`
  }
]

const DenoFrameworkPage = () => {
  return (
    <LayoutWebsite meta={{ title: framework, imageUrl: '/images/og/deno.png' }}>
      <SharedTemplate
        hero={{
          subTitle:
            'Build fast apps, serverless functions and more at the edge with Deno and GraphQL.',
          framework,
          svg: (
            <>
              <DenoIcon width={200} height={200} />
              <BackgroundSquares
                absolute
                css={{
                  left: -150,
                  top: -150,
                  zIndex: -1
                }}
              />
            </>
          )
        }}
        codeExamples={{
          subTitle:
            'Avoid the complicated setup. Build fast and secure apps with Deno.',
          framework,
          examples: codeExamples
        }}
        startBuilding={{ framework, columns: startBuilding }}
      />
    </LayoutWebsite>
  )
}

export default DenoFrameworkPage
